/**
 * <cylinder-component>
 *
 * Metaobject-driven cylinder subscription widget. Each dropdown option carries a
 * ready-made list of cart line items in `data-items` (built in Liquid from the
 * option's referenced products + their selling plan when `subscription` is on),
 * so selecting an option and adding to cart is a single, explicit path:
 *
 *     select option -> read data-items -> POST exactly those items to /cart/add.js
 *
 * No position math, no enable/disable hidden inputs, no special cases.
 */
class CylinderComponent extends HTMLElement {
  constructor() {
    super();

    this.zipEnabled = this.dataset.zip === 'true';
    this.moneyFormat =
      (window.theme && window.theme.moneyFormat) || this.dataset.moneyFormat || '${{amount}}';
    this.zips = window.zips || [];

    // gate flags
    this.zipOk = !this.zipEnabled; // no zip required -> already satisfied
    this.optionOk = false;

    this.currentItems = [];

    this.options = Array.from(this.querySelectorAll('[data-cyl-option]'));
    this.select = this.querySelector('[data-cyl-select]');
    this.selectList = this.querySelector('.cyl-selectList');
    this.selectToggle = this.querySelector('[data-cyl-select-toggle]');
    this.selectedName = this.querySelector('[data-cyl-selected-name]');
    this.priceEl = this.querySelector('[data-cyl-price]');
    this.receiveList = this.querySelector('[data-cyl-receive-list]');
    this.noteEl = this.querySelector('[data-cyl-note]');
    this.zipInput = this.querySelector('[data-cyl-zip-input]');
    this.zipError = this.querySelector('[data-cyl-zip-error]');
    this.howPopup = this.querySelector('[data-cyl-how-popup]');
    this.mobilePopup = this.querySelector('[data-cyl-mobile-popup]');
    this.overlay = this.querySelector('[data-cyl-overlay]');
    this.mobileOptions = Array.from(this.querySelectorAll('[data-cyl-mobile-option]'));

    // Kit card: a block-setting product rendered as a separate card above the
    // component. When selected the cart is exactly this product (one-time, qty 1).
    this.kitCard = this.querySelector('[data-cyl-kit]');
    this.mainCard = this.querySelector('[data-cyl-main-card]');
    this.mainHeader = this.mainCard ? this.mainCard.querySelector('.cyl-header') : null;
    this.kitSelected = false;
    this.selectedOptionEl = null;

    // Capture the check icon from the server-rendered markup up front, before
    // any re-render clears the receive list. Fall back to a USP check icon.
    const iconSrc =
      this.querySelector('.cyl-receiveItem .cyl-check') || this.querySelector('.cyl-check');
    this.checkIcon = iconSrc ? iconSrc.cloneNode(true) : null;

    this.onDocClick = this.onDocClick.bind(this);
    this.onAtcClick = this.onAtcClick.bind(this);
  }

  connectedCallback() {
    // Hijack the product form's add-to-cart trigger(s) inside this product so we
    // own the add. We swap their data-action to a private one; the generic
    // product-component handler keys off "add-all-to-cart" and no longer fires.
    this.productRoot = this.closest('product-component') || document;
    this.atcTriggers = Array.from(
      this.productRoot.querySelectorAll(
        '.ProductActions [data-action="add-all-to-cart"], .ProductActions [data-action="add-to-cart"]'
      )
    );
    this.atcTriggers.forEach((btn) => btn.setAttribute('data-action', 'cylinder-add'));

    // events
    this.options.forEach((opt) => {
      opt.addEventListener('click', () => this.selectOption(opt));
      opt.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectOption(opt);
        }
      });
    });

    if (this.selectToggle) {
      this.selectToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        // On mobile the cylinders selector opens a bottom-sheet (like the old
        // block); on desktop it opens the inline dropdown.
        if (this.isMobile() && this.mobilePopup) this.openMobilePopup();
        else this.toggleDropdown();
      });
    }
    document.addEventListener('click', this.onDocClick);

    // mobile bottom-sheet: delegated so it survives the move to <body>.
    // step 2 = pick a cylinder -> advance to step 3 (receive summary + add).
    if (this.mobilePopup) {
      this.mobilePopup.addEventListener('click', (e) => {
        if (e.target.closest('[data-cyl-mobile-close]')) return this.closeMobilePopup();
        if (e.target.closest('[data-cyl-mobile-back]')) return this.showMobileStep(2);
        if (e.target.closest('[data-cyl-mobile-add]')) return this.mobileAdd();
        const optEl = e.target.closest('[data-cyl-mobile-option]');
        if (optEl && !e.target.closest('[data-cyl-info-toggle]')) {
          this.onMobileSelect(parseInt(optEl.dataset.index, 10));
        }
      });
    }
    if (this.overlay) this.overlay.addEventListener('click', () => this.closeMobilePopup());

    if (this.zipInput) {
      this.zipInput.addEventListener('input', () => this.checkZip());
    }

    this.bindHowItWorks();
    this.bindInfoPopups();

    document.body.addEventListener('click', this.onAtcClick);

    // Initialise the main card's option state (no card activation yet) so its
    // price and line items agree from the first paint.
    const initial =
      this.options.find((o) => o.classList.contains('is-selected')) || this.options[0];
    if (initial) this.applyOption(initial);

    // Wire up the kit card and make it the default-selected card (matches the
    // old block + the screenshot). Clicking the component card's header selects
    // the refill service instead.
    if (this.kitCard) {
      this.kitItems = this.parseItems(this.kitCard.dataset.items);
      this.kitPrice = this.displayPrice(this.kitCard);
      this.kitAvailable = this.kitCard.dataset.available !== 'false';
      this.kitCard.addEventListener('click', () => this.selectKit());
      this.kitCard.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectKit();
        }
      });
      if (this.mainHeader) this.mainHeader.addEventListener('click', () => this.selectMain());
      this.selectKit({ silent: true });
    }
    this.refreshGate();

    // Fixed-position sheets must escape any transformed/filtered ancestor
    // (sticky bars, page animations) or they clip and mis-size. The old block
    // appends its popup straight to <body> for the same reason — do the same.
    if (this.mobilePopup) document.body.appendChild(this.mobilePopup);
    if (this.overlay) document.body.appendChild(this.overlay);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.body.removeEventListener('click', this.onAtcClick);
    if (this.mobilePopup && this.mobilePopup.parentNode) this.mobilePopup.remove();
    if (this.overlay && this.overlay.parentNode) this.overlay.remove();
  }

  /* ---------- dropdown ---------- */

  toggleDropdown() {
    const open = this.selectToggle.getAttribute('aria-expanded') === 'true';
    this.setDropdown(!open);
  }

  setDropdown(open) {
    this.selectToggle.setAttribute('aria-expanded', String(open));
    this.selectList.setAttribute('aria-hidden', String(!open));
  }

  isMobile() {
    return window.matchMedia('(max-width: 1024px)').matches;
  }

  openMobilePopup() {
    if (!this.mobilePopup) return;
    this.showMobileStep(2); // always open on the cylinders step
    this.mobilePopup.setAttribute('aria-visible', 'true');
    if (this.overlay) this.overlay.classList.add('is-visible');
    if (window.bodyScrollLock && typeof bodyScrollLock.disableBodyScroll === 'function') {
      bodyScrollLock.disableBodyScroll(
        this.mobilePopup.querySelector('[data-scrollable]') || this.mobilePopup,
        window.bodyScrollLockOptions
      );
    }
  }

  closeMobilePopup() {
    if (!this.mobilePopup) return;
    this.mobilePopup.setAttribute('aria-visible', 'false');
    if (this.overlay) this.overlay.classList.remove('is-visible');
    if (window.bodyScrollLock && typeof bodyScrollLock.clearAllBodyScrollLocks === 'function') {
      bodyScrollLock.clearAllBodyScrollLocks();
    }
  }

  // Show step 2 (cylinders) or step 3 (receive summary); the back button only
  // appears on step 3 — exactly like the old block.
  showMobileStep(step) {
    if (!this.mobilePopup) return;
    const step2 = this.mobilePopup.querySelector('[data-cyl-step="2"]');
    const step3 = this.mobilePopup.querySelector('[data-cyl-step="3"]');
    const back = this.mobilePopup.querySelector('[data-cyl-mobile-back]');
    if (step2) step2.style.display = step === 2 ? '' : 'none';
    if (step3) step3.style.display = step === 3 ? '' : 'none';
    if (back) back.style.display = step === 3 ? '' : 'none';
    // Reflect the ZIP gate as soon as step 3 is shown (like the old block):
    // disable Add to cart + reveal the "zip missing" note when no valid ZIP.
    if (step === 3) this.syncMobilePopupGate();
  }

  // Disable the step-3 Add button and show the "zip missing" note when the ZIP
  // gate isn't satisfied; enable + hide otherwise.
  syncMobilePopupGate() {
    if (!this.mobilePopup) return;
    const addBtn = this.mobilePopup.querySelector('[data-cyl-mobile-add]');
    const note = this.mobilePopup.querySelector('.product__mobile-gas-error-msg');
    const zipMissing = this.zipEnabled && !this.zipOk;
    if (addBtn) {
      if (zipMissing) addBtn.setAttribute('disabled', 'true');
      else addBtn.removeAttribute('disabled');
    }
    if (note) note.setAttribute('aria-hidden', zipMissing ? 'false' : 'true');
  }

  onMobileSelect(idx) {
    if (this.options[idx]) {
      this.selectOption(this.options[idx]);
      this.populateMobileStep3(this.options[idx]);
    }
    this.showMobileStep(3);
  }

  // Fill step 3 with the chosen option's receive lines + note (the "different
  // window" the old block shows after picking a cylinder).
  populateMobileStep3(opt) {
    const list = this.mobilePopup.querySelector('[data-cyl-mobile-receive]');
    const noteEl = this.mobilePopup.querySelector('[data-cyl-mobile-note]');
    if (list) {
      let lines = [];
      try {
        lines = JSON.parse(opt.dataset.receive || '[]');
      } catch (err) {
        lines = [];
      }
      list.innerHTML = '';
      lines.forEach((line) => {
        const row = document.createElement('div');
        row.className = 'gasExchange--USP';
        if (this.checkIcon) {
          const ck = document.createElement('div');
          ck.className = 'gasExchange--Checkmark';
          ck.appendChild(this.checkIcon.cloneNode(true));
          row.appendChild(ck);
        }
        const text = document.createElement('div');
        text.className = 'u-p3';
        text.textContent = line;
        row.appendChild(text);
        list.appendChild(row);
      });
    }
    if (noteEl) noteEl.textContent = opt.dataset.note || '';
  }

  mobileAdd() {
    if (this.zipEnabled && !this.zipOk) {
      this.syncMobilePopupGate();
      return;
    }
    if (!this.optionOk || !this.currentItems.length) return;
    const trigger = this.mobilePopup.querySelector('[data-cyl-mobile-add]') || this.atcTriggers[0];
    this.addToCart(trigger);
    this.closeMobilePopup();
  }

  onDocClick(e) {
    if (this.select && !this.select.contains(e.target)) this.setDropdown(false);
    // close any open info / how-it-works popup when clicking outside its wrapper
    this.querySelectorAll('[data-cyl-info-popup], [data-cyl-how-popup]').forEach((popup) => {
      const wrap = popup.closest('.cyl-infoWrap, .cyl-howWrap');
      if (wrap && !wrap.contains(e.target)) {
        popup.setAttribute('aria-hidden', 'true');
      }
    });
  }

  // Close every info / how-it-works popup except (optionally) one. The toggles
  // stopPropagation (so onDocClick can't run), so opening one popup must
  // explicitly dismiss the others - otherwise clicking a second trigger leaves
  // the first one open.
  closeInfoPopups(except) {
    this.querySelectorAll('[data-cyl-info-popup], [data-cyl-how-popup]').forEach((popup) => {
      if (popup !== except) popup.setAttribute('aria-hidden', 'true');
    });
  }

  bindInfoPopups() {
    this.querySelectorAll('[data-cyl-info-toggle]').forEach((toggle) => {
      // Support both wrapper shapes: the inline selectors (.cyl-infoWrap) and the
      // mobile sheet's cylinders header (.gasExchange--cylindersHeader).
      const wrap = toggle.closest('.cyl-infoWrap, .gasExchange--cylindersHeader');
      if (!wrap) return;
      const popup = wrap.querySelector('[data-cyl-info-popup]');
      const close = wrap.querySelector('[data-cyl-info-close]');
      if (!popup) return;

      const setOpen = (open) => popup.setAttribute('aria-hidden', String(!open));

      // tap / click toggles (works on every breakpoint)
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const willOpen = popup.getAttribute('aria-hidden') !== 'false';
        // only one popup open at a time - dismiss the others first
        this.closeInfoPopups(popup);
        setOpen(willOpen);
      });
      // the × always closes it
      if (close) {
        close.addEventListener('click', (e) => {
          e.stopPropagation();
          setOpen(false);
        });
      }
      // desktop: also reveal on hover (like the old block), via JS so the × still
      // works while hovering
      wrap.addEventListener('mouseenter', () => {
        if (!this.isMobile()) {
          this.closeInfoPopups(popup);
          setOpen(true);
        }
      });
      wrap.addEventListener('mouseleave', () => {
        if (!this.isMobile()) setOpen(false);
      });
    });
  }

  /* ---------- option selection ---------- */

  parseItems(json) {
    try {
      return JSON.parse(json || '[]');
    } catch (err) {
      return [];
    }
  }

  // Apply a dropdown option's data (items, label, price, receive lines, note)
  // WITHOUT changing which top-level card is active.
  applyOption(opt) {
    this.options.forEach((o) => o.classList.remove('is-selected'));
    opt.classList.add('is-selected');
    this.selectedOptionEl = opt;

    // line items for this option
    this.currentItems = this.parseItems(opt.dataset.items);
    this.optionOk = opt.dataset.available !== 'false' && this.currentItems.length > 0;

    // label
    if (this.selectedName) this.selectedName.textContent = opt.dataset.name || '';

    // price
    const price = this.displayPrice(opt);
    if (this.priceEl) this.priceEl.textContent = price;
    this.updateButtonPrice(price);

    // "you will receive" lines
    this.renderReceive(opt);

    // note
    if (this.noteEl) {
      const note = opt.dataset.note || '';
      this.noteEl.textContent = note;
      if (note) this.noteEl.removeAttribute('hidden');
      else this.noteEl.setAttribute('hidden', '');
    }
  }

  // Picking a cylinder option means choosing the component (refill) card, so
  // make it the active card when a kit card is present.
  selectOption(opt, { silent = false } = {}) {
    this.applyOption(opt);
    if (this.kitCard) this.activateMain();
    this.setDropdown(false);
    if (!silent) this.refreshGate();
  }

  /* ---------- kit card (block-setting product) ---------- */

  activateKit() {
    if (this.mainCard) {
      this.mainCard.classList.remove('is-active');
      this.mainCard.setAttribute('aria-selected', 'false');
    }
    if (this.kitCard) {
      this.kitCard.classList.add('is-active');
      this.kitCard.setAttribute('aria-selected', 'true');
    }
    this.kitSelected = true;
  }

  activateMain() {
    if (this.kitCard) {
      this.kitCard.classList.remove('is-active');
      this.kitCard.setAttribute('aria-selected', 'false');
    }
    if (this.mainCard) {
      this.mainCard.classList.add('is-active');
      this.mainCard.setAttribute('aria-selected', 'true');
    }
    this.kitSelected = false;
  }

  // Select the kit: cart becomes exactly the kit product (one-time, qty 1).
  selectKit({ silent = false } = {}) {
    if (!this.kitCard) return;
    this.activateKit();
    this.currentItems = this.kitItems || [];
    this.optionOk = this.kitAvailable && this.currentItems.length > 0;
    this.updateButtonPrice(this.kitPrice);
    this.setDropdown(false);
    if (!silent) this.refreshGate();
  }

  // Select the component (refill) card, restoring the last-chosen dropdown option.
  selectMain({ silent = false } = {}) {
    this.activateMain();
    if (this.selectedOptionEl) this.applyOption(this.selectedOptionEl);
    if (!silent) this.refreshGate();
  }

  renderReceive(opt) {
    if (!this.receiveList) return;
    let lines = [];
    try {
      lines = JSON.parse(opt.dataset.receive || '[]');
    } catch (err) {
      lines = [];
    }
    this.receiveList.innerHTML = '';
    lines.forEach((line) => {
      const li = document.createElement('li');
      li.className = 'cyl-receiveItem';
      const span = document.createElement('span');
      span.className = 'u-p3';
      span.textContent = line;
      if (this.checkIcon) li.appendChild(this.checkIcon.cloneNode(true));
      li.appendChild(span);
      this.receiveList.appendChild(li);
    });
  }

  /* ---------- zip ---------- */

  checkZip() {
    if (!this.zipEnabled || !this.zipInput) return;
    const value = this.zipInput.value.trim();
    // Read the list at check-time: `zip_codes` sets window.zips and may load
    // after this element upgrades.
    const zips = window.zips || this.zips || [];

    if (zips.includes(value) && value.length === 5) {
      this.zipOk = true;
      this.zipInput.classList.remove('is-error');
      this.hideZipError();
    } else if (value.length >= 5) {
      this.zipOk = false;
      this.zipInput.classList.add('is-error');
      this.showZipError();
    } else {
      this.zipOk = false;
      this.zipInput.classList.remove('is-error');
      this.hideZipError();
    }
    this.refreshGate();
  }

  showZipError() {
    if (!this.zipError) return;
    this.zipError.setAttribute('aria-hidden', 'false');
    clearTimeout(this._zipTimer);
    this._zipTimer = setTimeout(() => this.hideZipError(), 5000);
  }

  hideZipError() {
    if (this.zipError) this.zipError.setAttribute('aria-hidden', 'true');
  }

  /* ---------- add-to-cart gate + button ---------- */

  refreshGate() {
    // The kit is a one-time machine purchase — it never requires a ZIP.
    const ready = this.kitSelected ? this.kitAvailable : this.zipOk && this.optionOk;
    this.getAtcButtons().forEach((btn) => {
      if (ready) btn.removeAttribute('disabled');
      else btn.setAttribute('disabled', 'true');
    });
  }

  getAtcButtons() {
    return Array.from(
      (this.productRoot || document).querySelectorAll('.Product--AddToCartButton')
    );
  }

  updateButtonPrice(price) {
    if (!price) return;
    (this.productRoot || document)
      .querySelectorAll('.ProductPage--ATC .button--money')
      .forEach((el) => {
        el.textContent = price;
      });
  }

  onAtcClick(e) {
    // Only handle the trigger(s) we hijacked for this product.
    const trigger = e.target.closest('[data-action="cylinder-add"]');
    if (!trigger || !this.atcTriggers.includes(trigger)) return;
    e.preventDefault();
    this.addToCart(trigger);
  }

  addToCart(trigger) {
    // honour the gate (keyboard / programmatic clicks bypass pointer-events)
    if (trigger.querySelector('.Product--AddToCartButton[disabled]')) return;
    if (!this.optionOk) {
      this.showError(trigger, this.dataset.unavailable || 'This option is currently unavailable.');
      return;
    }
    if (!this.kitSelected && this.zipEnabled && !this.zipOk) return;
    if (!this.currentItems.length) return;

    const buttons = this.productRoot.querySelectorAll('.AddToCartAjax');
    buttons.forEach((b) => b.setAttribute('disabled', 'disabled'));
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: this.currentItems }),
    })
      .then((response) => {
        if (response.ok) {
          this.dispatchEvent(
            new CustomEvent('product:added', { bubbles: true, detail: { quantity: this.currentItems.length } })
          );
          return null;
        }
        return response.json().then((content) => {
          this.showError(trigger, content.description || 'Something went wrong.');
        });
      })
      .catch(() => {})
      .finally(() => {
        buttons.forEach((b) => b.removeAttribute('disabled'));
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      });
  }

  showError(trigger, message) {
    const el = document.createElement('span');
    el.className = 'product-form__unavailable Alert Alert--error u-p4';
    el.innerHTML = message;
    trigger.insertAdjacentElement('afterend', el);
    setTimeout(() => el.remove(), 4000);
  }

  /* ---------- how it works popup ---------- */

  bindHowItWorks() {
    const toggle = this.querySelector('[data-cyl-how-toggle]');
    const close = this.querySelector('[data-cyl-how-close]');
    if (toggle && this.howPopup) {
      toggle.addEventListener('click', () => {
        const willOpen = this.howPopup.getAttribute('aria-hidden') !== 'false';
        this.closeInfoPopups(this.howPopup);
        this.howPopup.setAttribute('aria-hidden', String(!willOpen));
      });
    }
    if (close && this.howPopup) {
      close.addEventListener('click', () => this.howPopup.setAttribute('aria-hidden', 'true'));
    }
  }

  /* ---------- helpers ---------- */

  // The price to show for a card / option. Prefer the string Liquid already
  // rendered (data-price-formatted): Shopify's money filter applies the
  // currency's own locale conventions, which Shopify.formatMoney can only
  // approximate from the placeholder name - so re-formatting the raw cents
  // here produces a different separator from the prices rendered on the page
  // (e.g. "2,899 kr" against Liquid's "2 899 kr" in SEK). Falls back to
  // formatting the cents when the attribute is absent.
  displayPrice(el) {
    if (!el) return '';
    return el.dataset.priceFormatted || this.formatMoney(parseInt(el.dataset.price, 10) || 0);
  }

  formatMoney(cents) {
    let out = cents;
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      out = Shopify.formatMoney(cents, this.moneyFormat);
    }
    return String(out).replace('.00', '');
  }
}

customElements.define('cylinder-component', CylinderComponent);
